import { buildSearchQuery, isBuyNowQuery } from '@/utils/searchQuery';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { fetchCatalogAdIfNeeded } from '@/redux/modules/catalogAd';
import { fetchItemCardModelsIfNeeded, getItemCardModel } from '@/redux/modules/itemCardModel';
import { fetchItemFacetsIfNeeded, getItemFacets } from '@/redux/modules/itemFacets';
import {
    fetchSearchFacetsIfNeeded,
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
} from '@/redux/modules/searchFacets';
import { fetchSearchSuggestions, getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions';
import { fetchSellersIfNeeded } from '@/redux/modules/seller';
import { fetchSimilarItemsIfNeeded, getSimilarItemIds } from '@/redux/modules/similarItems';
import {
    getCategoryIdsFromQueryParams,
    getKeywordFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '@/utils/queryParams';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { getSearchedItemIds, getSearchUiState, submitSearch } from '@/redux/modules/search';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { isUserLoggedIn } from '@/redux/modules/user';
import { submitIsSavedSearchCheck } from '@/redux/modules/saveSearchCheck';
import cleanSearchTerms from '@/utils/cleanSearchTerms';

export const DATA_LOADING_ID = 'results-plus-promoted';

export default ({ location, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    return Promise.all([dispatch(fetchSearchFacetsIfNeeded(archivedSearch))]).then(() => {
        const currentState = getState();
        const pageSize = getSearchPageSize(currentState);
        let rawFacets;
        if (archivedSearch) {
            rawFacets = getRawArchivedSearchFacets(currentState);
        } else {
            rawFacets = getRawUpcomingSearchFacets(currentState);
        }
        const searchQuery = buildSearchQuery({ pathname, queryParams, rawFacets, searchPageSize: pageSize });
        const isBIN = isBuyNowQuery(searchQuery);
        searchQuery.buyNow = !archivedSearch && isBIN;
        const { searchTerm } = searchQuery;
        const pageName = searchQuery.options.status.includes('sold') ? 'sold-search' : 'search';
        const keywords = getKeywordFromQueryParams(queryParams);
        const userLoggedIn = isUserLoggedIn(currentState);

        const promises = [
            dispatch(submitSearch(searchQuery, pageName)),
            dispatch(fetchSearchSuggestions(cleanSearchTerms(searchTerm), true)),
        ];

        if (userLoggedIn) {
            const ssParams = getSaveSearchParams(queryParams);
            promises.push(dispatch(submitIsSavedSearchCheck(ssParams)));
        }

        return Promise.all(promises).then(() => {
            const searchState = getState();
            const itemIds = getSearchedItemIds(searchState);

            // If we have search results do all the stuff to get the data for them.
            if (itemIds.length) {
                const { houses } = getSecondarySearchSuggestions(searchState);

                const promises = [
                    dispatch(
                        fetchCatalogAdIfNeeded({
                            categoryIds: getCategoryIdsFromQueryParams(queryParams),
                            keywords,
                            pageName,
                        })
                    ),
                ];

                if (houses.length) {
                    promises.push(dispatch(fetchSellersIfNeeded([houses[0]?.houseId])));
                }

                return Promise.all(promises);

                // If we get no results back, we'll get the data for our zero results page
            } else {
                const { soldItemIds = [] } = getSearchUiState(searchState);
                const promises = [
                    dispatch(fetchItemCardModelsIfNeeded(soldItemIds, 'SEARCH-PAGE-SOLD')),
                    dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
                ];

                return Promise.all(promises).then(() => {
                    const state = getState();
                    const { itemId, sellerId, title } = getItemCardModel(state, soldItemIds[0]);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsIfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, [itemId]);
                        return dispatch(fetchItemCardModelsIfNeeded(similarItemIds, 'SEARCH-PAGE-SIMILAR'));
                    });
                });
            }
        });
    });
};
